import { faker } from "@faker-js/faker";
import { OrderStatus, PaymentMethodsType } from "@/types";
import neighborhoods from "@/constants/neighborhoods";
import { useAddOrderMutation, useUpdateOrderMutation } from "@/features/organizations/api/ordersApi";
import { useFetchContentQuery as useFetchOrganizationContentQuery } from "@/features/organizations/api/contentApi";
import { useAddUserOrgOrderMutation } from "@/api/userOrgOrdersApi";
import { ORDER_STATUS_TIMESTAMP_MAP } from "@/constants/orders";
import { calculateOrderTotals } from "@/utils/order";
import { OrderItem } from "@/types/items";


export const useGenerateFakeOrders = (organizationId: string | undefined) => {
  const [addOrder] = useAddOrderMutation();
  const [updateOrder] = useUpdateOrderMutation();
  const [addUserOrgOrder] = useAddUserOrgOrderMutation();
  const { data: orgContent } = useFetchOrganizationContentQuery(organizationId ?? "");

  const generateFakeOrders = async () => {
    const rawCount = prompt("¿Cuántas órdenes desea crear?", "1");
    const count = parseInt(rawCount || "0");
    if (isNaN(count) || count <= 0) return;

    const statusInput = prompt(
      "Ingrese el estado de la orden (draft, rejected, cancelled, reviewing, preparing, sending, delivered, completed, archived)",
      "preparing"
    );
    const status = (statusInput as OrderStatus) || OrderStatus.PREPARING;

    if (!organizationId) return;

    for (let i = 0; i < count; i++) {
      const selectedNeighborhood = faker.helpers.arrayElement(neighborhoods);
      const itemsCount = faker.number.int({ min: 1, max: 5 });
      const selectedItems = orgContent ? faker.helpers.arrayElements(orgContent as any[], itemsCount) : [];

      const items = selectedItems.map((item: any) => ({
        id: faker.string.uuid(),
        itemId: item.id,
        title: item.title,
        price: item.price,
        quantity: faker.number.int({ min: 1, max: 3 }),
        status: "preparing",
      }));

      const { subtotal, taxes, total } = calculateOrderTotals(items as unknown as OrderItem[]);


      // STEP 1: Create Order with allowed fields
      const creationPayload: any = {
        number: faker.number.int({ min: 1000, max: 9999 }),
        status,
        organizationId,
        createdAt: Date.now(),
        orderedAt: status !== OrderStatus.DRAFT ? Date.now() : undefined,
        items,
        subtotal,
        taxes,
        total,
        flow: faker.helpers.arrayElement(["delivery", "pickup", "dine-in"]),
        source: faker.helpers.arrayElement(["whatsapp", "web", "admin"]),
      };

      // Add relevant timestamps based on status
      const timestampKey = ORDER_STATUS_TIMESTAMP_MAP[status];
      if (timestampKey) {
        creationPayload[timestampKey] = Date.now();
      }

      // If status implies approval, also set approvedAt if not already set by timestampKey
      const approvalStatuses = [OrderStatus.PREPARING, OrderStatus.SENDING, OrderStatus.DELIVERED, OrderStatus.COMPLETED];
      if (approvalStatuses.includes(status) && timestampKey !== "approvedAt") {
        creationPayload.approvedAt = Date.now();
      }

      const creationResult: any = await addOrder({ organizationId, order: creationPayload }).unwrap();

      if (creationResult?.id) {
        // STEP 2: Update Order with remaining fields (client, location, payment)
        const updatePayload = {
          id: creationResult.id,
          paymentDetails: {
            method: faker.helpers.arrayElement([PaymentMethodsType.CASH, PaymentMethodsType.CREDIT_CARD, PaymentMethodsType.BANK_TRANSFER]),
            value: null,
          },
          deliveryLocation: {
            id: faker.string.uuid(),
            address: faker.location.streetAddress(),
            neighborhood: selectedNeighborhood.title,
            neighborhoodId: selectedNeighborhood.slug,
            latitude: selectedNeighborhood.lat,
            longitude: selectedNeighborhood.lng,
            distance: { text: "0 km", value: 0 },
            duration: { text: "0 min", value: 0 },
          },
          client: {
            id: faker.string.uuid(),
            name: faker.person.firstName(),
            lastname: faker.person.lastName(),
            email: faker.internet.email(),
            phone: faker.phone.number(),
            sanitizedName: "",
            sanitizedLastname: "",
          },
        };

        await updateOrder({ organizationId, order: updatePayload }).unwrap();

        // Also add to userOrgOrders
        await addUserOrgOrder({
          organizationId,
          userId: updatePayload.client.id,
          orderId: creationResult.id,
          orderedAt: creationPayload.orderedAt || Date.now(),
          subtotal: creationPayload.subtotal,
          total: creationPayload.total,
        });
      }
    }

    alert(`${count} órdenes fakes creadas exitosamente.`);
  };

  return { generateFakeOrders };
};
